'use client';

import { useQrCodeGeneratorStore } from '@/components/provider/QrCodeConfigStoreProvider';
import { DynamicQrCode } from '@/components/qr-generator/DynamicQrCode';
import { useGetReservedShortUrlQuery } from '@/lib/api/url-shortener';
import { useHasProPlan } from '@/hooks/useHasProPlan';
import { ScannerPreviewFrame } from './ScannerPreviewFrame';
import type { CreatorExperienceId } from './experience-config';

export function CreatorReviewStep({ experience }: { experience: CreatorExperienceId }) {
	const { content, config, name, updateName } = useQrCodeGeneratorStore((state) => state);
	const { data: shortUrl } = useGetReservedShortUrlQuery();
	const { hasProPlan } = useHasProPlan();
	const isDynamic =
		(content.type === 'url' || content.type === 'vCard') && content.data.isDynamic === true;
	const destination =
		content.type === 'url'
			? content.data.url
			: content.type === 'wifi'
				? `Wi-Fi Network: ${content.data.ssid}`
				: content.type === 'event'
					? `Calendar Event: ${content.data.title}`
					: content.type === 'vCard'
						? `Contact Card: ${[content.data.firstName, content.data.lastName].filter(Boolean).join(' ') || content.data.company || 'vCard'}`
						: '';

	return (
		<section aria-labelledby="review-step-title" className="space-y-6">
			<div>
				<h1 id="review-step-title" className="text-2xl font-bold tracking-tight text-slate-950">
					Review and download
				</h1>
				<p className="mt-2 text-slate-600">
					Scan the preview with your phone before you print or share it.
				</p>
			</div>
			<div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_300px]">
				<div className="space-y-5">
					<div className="rounded-xl border border-slate-200 bg-white p-5">
						<label htmlFor="qr-name" className="mb-2 block text-sm font-medium">
							QR code name <span className="font-normal text-slate-500">(optional)</span>
						</label>
						<input
							id="qr-name"
							name="qrName"
							maxLength={50}
							autoComplete="off"
							value={name ?? ''}
							onChange={(event) => updateName(event.target.value)}
							placeholder={
								experience === 'website'
									? 'My website'
									: experience === 'whatsapp'
										? 'WhatsApp contact'
										: experience === 'business-card'
											? 'My business card'
											: experience === 'social-links'
												? 'My social links'
												: experience === 'menu'
													? 'My restaurant menu'
													: experience === 'pdf'
														? 'My PDF document'
														: experience === 'coupon'
															? 'My promo coupon'
															: experience === 'wifi'
																? 'My Wi-Fi network'
																: experience === 'event'
																	? 'My calendar event'
																	: experience === 'vcard'
																		? 'My vCard contact'
																		: experience === 'app'
																			? 'My mobile app'
																			: 'My QR code'
							}
							className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
						/>
					</div>
					<div className="rounded-xl border border-slate-200 bg-white p-5">
						<div className="flex items-center gap-2">
							<span
								className={`size-2 rounded-full ${isDynamic ? 'bg-emerald-500' : 'bg-slate-400'}`}
							/>
							<h2 className="font-semibold">{isDynamic ? 'Dynamic QR code' : 'Static QR code'}</h2>
						</div>
						<p className="mt-2 text-sm leading-6 text-slate-600">
							{isDynamic
								? 'You can change the destination later. Ekutu QR counts scans; destination actions such as sending a WhatsApp message happen in the destination app and are not yet measured.'
								: 'This code points directly to the destination and cannot be changed after it is printed.'}
						</p>
						<div
							className="mt-4 break-all rounded-lg bg-slate-50 p-3 text-sm text-slate-700"
							aria-label="QR destination"
						>
							{destination}
						</div>
						{destination && (
							<a
								href={destination}
								target="_blank"
								rel="noopener noreferrer"
								className="mt-3 inline-flex text-sm font-medium text-blue-700 underline underline-offset-4"
							>
								Test destination before printing
							</a>
						)}
					</div>
				</div>
				<div>
					<ScannerPreviewFrame>
						<DynamicQrCode
							qrCode={{ content, config }}
							shortUrl={shortUrl || undefined}
							hasProPlan={hasProPlan}
						/>
					</ScannerPreviewFrame>
				</div>
			</div>
		</section>
	);
}
